import { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { supabase } from "@/integrations/supabase/client";
import { useDignityAudio } from "@/hooks/useDignityAudio";
import { useTheme } from "@/contexts/ThemeContext";
import { Play, Pause, SkipForward, SkipBack, Volume2, VolumeX, X, ChevronRight, Globe } from "lucide-react";
import { useLanguage } from "@/hooks/useLanguage";
import { useBrand } from "@/config/brands";
import type { LanguageCode } from "@/i18n";

interface NarrationLine {
  id: string;
  line_number: number;
  display_text: string;
  audio_url: string | null;
  audio_duration_ms: number | null;
  word_timestamps: { word: string; start_ms: number; end_ms: number }[] | null;
}

interface AvailableLanguage {
  code: string;
  name: string;
  hasAudio: boolean;  // true if at least 1 line has audio_url
  lineCount: number;
}

interface DignityNarrationPlayerProps {
  scenarioId?: string;
  procedureCode?: string;
  codeType?: string;
  pageKey?: string;
  language?: string;
  onLineChange?: (lineIdx: number) => void;
  /** When true, narration plays inline without the full-screen overlay — page stays visible */
  noOverlay?: boolean;
  /** When true, the launch control renders as a slim horizontal button (for the
   *  two-guide Dignity hub) instead of the tall centered "has something to tell
   *  you" block. The overlay/playback behavior is unchanged. */
  compact?: boolean;
  /** Compact-mode primary label (e.g. "Walk me through this scenario"). */
  label?: string;
  /** Compact-mode secondary label; falls back to "N lines · Listen/Read". */
  sublabel?: string;
  /** Opens the overlay and starts line 1 as soon as lines load, instead of
   *  rendering the trigger card and waiting for a click. For callers that are
   *  themselves the play button and only mount this once the user has asked. */
  autoStart?: boolean;
  /** Fired when the user dismisses the overlay, so an autoStart caller can
   *  unmount rather than leave the trigger card behind on the page. */
  onClose?: () => void;
}

const serif = "'Instrument Serif', Georgia, serif";
const sans = "'Instrument Sans', system-ui, sans-serif";

// Language → country code mapping for flag images (flagcdn.com)
const LANG_COUNTRY: Record<string, string> = {
  en: "us", es: "es", zh: "cn", fr: "fr", vi: "vn", ko: "kr",
  ar: "sa", tl: "ph", ru: "ru", pt: "br", de: "de", ja: "jp",
  hi: "in", it: "it", pl: "pl", uk: "ua", fa: "ir", th: "th",
  bn: "bd", sw: "ke", ht: "ht", ur: "pk", my: "mm", so: "so",
};
const langFlagImg = (lang: string, size = 20) => {
  const cc = LANG_COUNTRY[lang];
  if (!cc) return null;
  return <img src={`https://flagcdn.com/w40/${cc}.png`} alt={lang} width={size} height={Math.round(size * 0.7)} style={{ borderRadius: 2 }} />;
};

// Footer heights to offset above the app footer bar
const FOOTER_MOBILE = 80;
const FOOTER_DESKTOP = 64;
const FOOTER_PAD = 8; // extra breathing room

/**
 * Brand gate. Dignity is AHP's mascot, and this player says her name out loud —
 * trigger labels, the overlay, the narration itself. It is mounted from 10
 * different pages (state comparison, rankings, scenarios, price plan, hospital
 * profile, …), so gating each caller would mean ten chances to miss one; two of
 * them had already leaked onto Luigi's and AntiGreed.
 *
 * Implemented as a wrapper rather than an early return inside the component:
 * bailing out after useBrand() but before the ~15 hooks below would be a
 * conditional-hook pattern. This keeps hook order identical on every render.
 */
export function DignityNarrationPlayer(props: DignityNarrationPlayerProps) {
  const brand = useBrand();
  if (brand.key !== 'ahp') return null;
  return <DignityNarrationPlayerImpl {...props} />;
}

function DignityNarrationPlayerImpl({
  scenarioId,
  procedureCode,
  codeType,
  pageKey,
  language = "en",
  onLineChange,
  noOverlay = false,
  compact = false,
  label,
  sublabel,
  autoStart = false,
  onClose,
}: DignityNarrationPlayerProps) {
  const { isDark } = useTheme();
  const { changeLanguage: changeSiteLanguage } = useLanguage();
  const { playAudioSrc, stopAudio, pauseAudio, resumeAudio, volume, setVolume, preloadUrls, isPlaying, isPaused } = useDignityAudio(0.5);

  const [lines, setLines] = useState<NarrationLine[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentLineIdx, setCurrentLineIdx] = useState(-1);
  const [currentWordIdx, setCurrentWordIdx] = useState(-1);
  const [narrating, setNarrating] = useState(false);
  const [overlayOpen, setOverlayOpen] = useState(false);
  const [autoAdvance, setAutoAdvance] = useState(true);
  const [lineFinished, setLineFinished] = useState(false);
  const [showVolume, setShowVolume] = useState(false);
  const [hoveredLine, setHoveredLine] = useState<number | null>(null);
  const [activeLang, setActiveLang] = useState(language);
  const [availableLangs, setAvailableLangs] = useState<AvailableLanguage[]>([]);
  const [showLangPicker, setShowLangPicker] = useState(false);
  const [langQuery, setLangQuery] = useState("");
  const langSearchRef = useRef<HTMLInputElement>(null);
  const wordTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const playbackStartRef = useRef<number>(0);
  const autoAdvanceRef = useRef(true);
  const autoAdvanceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dialogueBoxRef = useRef<HTMLDivElement>(null);

  // Keep ref in sync
  useEffect(() => { autoAdvanceRef.current = autoAdvance; }, [autoAdvance]);

  // VN-style dialogue window: each DB "line" is a whole paragraph, so the box
  // is clamped to ~3 visual lines and auto-scrolls to keep the highlighted
  // word on the middle line as narration progresses (classic visual-novel
  // text reveal — never a wall of text).
  useEffect(() => {
    const box = dialogueBoxRef.current;
    if (!box || currentWordIdx < 0) return;
    const el = box.querySelector<HTMLElement>(".dignity-active-word");
    if (!el) return;
    const lineH = parseFloat(getComputedStyle(box).lineHeight) || 26;
    const target = Math.max(0, el.offsetTop - lineH);
    if (Math.abs(box.scrollTop - target) > lineH / 2) {
      box.scrollTo({ top: target, behavior: "smooth" });
    }
  }, [currentWordIdx]);

  // Reset the dialogue scroll to the top whenever the line changes.
  useEffect(() => {
    dialogueBoxRef.current?.scrollTo({ top: 0 });
  }, [currentLineIdx]);

  // Notify parent when line changes
  useEffect(() => { onLineChange?.(currentLineIdx); }, [currentLineIdx, onLineChange]);

  // Whether current language has audio for its lines
  const hasAudio = lines.some(l => !!l.audio_url);

  // Fetch available languages for this scenario/code
  useEffect(() => {
    const fetchLangs = async () => {
      let query = supabase
        .from("dignity_narration_lines")
        .select("language, audio_url")
        .eq("is_active", true);

      if (pageKey) {
        query = query.eq("page_key", pageKey);
      } else if (scenarioId) {
        query = query.eq("scenario_id", scenarioId);
      } else if (procedureCode && codeType) {
        query = query.eq("procedure_code", procedureCode).eq("code_type", codeType);
      } else return;

      const { data } = await query;
      if (!data) return;

      // Group by language — track total + audio-bearing line counts
      const langMap = new Map<string, { linesWithAudio: number; count: number }>();
      for (const row of data) {
        const lang = row.language || "en";
        const entry = langMap.get(lang) || { linesWithAudio: 0, count: 0 };
        entry.count++;
        if (row.audio_url) entry.linesWithAudio++;
        langMap.set(lang, entry);
      }

      // Also fetch language names from voice config
      const { data: voiceConfigs } = await supabase
        .from("dignity_voice_config")
        .select("language, language_name")
        .eq("is_active", true);
      const nameMap = new Map<string, string>();
      for (const vc of voiceConfigs || []) {
        nameMap.set(vc.language, vc.language_name);
      }

      const langs: AvailableLanguage[] = [];
      for (const [code, info] of langMap) {
        if (info.linesWithAudio === 0) continue;
        langs.push({
          code,
          name: nameMap.get(code) || code.toUpperCase(),
          hasAudio: true,
          lineCount: info.count,
        });
      }
      // Sort: current language first, then alphabetical
      langs.sort((a, b) => {
        if (a.code === language) return -1;
        if (b.code === language) return 1;
        return a.name.localeCompare(b.name);
      });
      setAvailableLangs(langs);
    };
    fetchLangs();
  }, [scenarioId, procedureCode, codeType, pageKey, language]);

  const T = {
    blue: "#002868",
    gold: "#C5A55A",
    goldDim: isDark ? "rgba(197,165,90,0.35)" : "rgba(197,165,90,0.5)",
    red: "#B22234",
    text: isDark ? "#f0ebe3" : "#1B1B1B",
    textMuted: isDark ? "#c4bcae" : "#6b6560",
    textDim: isDark ? "rgba(240,235,227,0.25)" : "rgba(27,27,27,0.18)",
    dialogBg: isDark
      ? "linear-gradient(180deg, rgba(0,16,52,0.98) 0%, rgba(0,10,35,0.99) 100%)"
      : "linear-gradient(180deg, rgba(255,255,255,0.99) 0%, rgba(248,246,241,0.99) 100%)",
    dialogBorder: isDark ? "rgba(197,165,90,0.4)" : "rgba(0,40,104,0.2)",
    dialogShadow: isDark
      ? "0 -8px 60px rgba(0,20,60,0.7), 0 0 0 1px rgba(197,165,90,0.2)"
      : "0 -8px 60px rgba(0,20,60,0.12), 0 0 0 1px rgba(0,40,104,0.1)",
    activeWord: "#C5A55A",
    spokenWord: isDark ? "#f0ebe3" : "#1B1B1B",
    unspokenWord: isDark ? "rgba(240,235,227,0.3)" : "rgba(27,27,27,0.25)",
  };

  // Fetch narration lines from DB
  useEffect(() => {
    let cancelled = false;
    const fetchLines = async () => {
      setLoading(true);
      let query = supabase
        .from("dignity_narration_lines")
        .select("id, line_number, display_text, audio_url, audio_duration_ms, word_timestamps")
        .eq("is_active", true)
        .eq("language", activeLang)
        .order("line_number", { ascending: true });

      if (pageKey) {
        query = query.eq("page_key", pageKey);
      } else if (scenarioId) {
        query = query.eq("scenario_id", scenarioId);
      } else if (procedureCode && codeType) {
        query = query.eq("procedure_code", procedureCode).eq("code_type", codeType);
      } else {
        setLoading(false);
        return;
      }

      const { data, error } = await query;
      if (cancelled) return;

      if (error) {
        console.error("[NarrationPlayer] fetch error:", error);
        setLoading(false);
        return;
      }

      setLines((data as NarrationLine[]) || []);
      setLoading(false);

      const urls = (data || []).map((l: any) => l.audio_url).filter(Boolean) as string[];
      if (urls.length) preloadUrls(urls);
    };

    fetchLines();
    return () => { cancelled = true; };
  }, [scenarioId, procedureCode, codeType, pageKey, activeLang, preloadUrls]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopAudio();
      if (wordTimerRef.current) clearInterval(wordTimerRef.current);
      if (autoAdvanceTimerRef.current) clearTimeout(autoAdvanceTimerRef.current);
    };
  }, [stopAudio]);

  // Close lang picker when clicking outside
  useEffect(() => {
    if (!showLangPicker) return;
    const handler = () => setShowLangPicker(false);
    // Defer so the opening click doesn't immediately close
    const id = setTimeout(() => document.addEventListener("click", handler), 0);
    return () => { clearTimeout(id); document.removeEventListener("click", handler); };
  }, [showLangPicker]);

  // Reset search + focus input when picker opens
  useEffect(() => {
    if (!showLangPicker) { setLangQuery(""); return; }
    const id = setTimeout(() => langSearchRef.current?.focus(), 30);
    return () => clearTimeout(id);
  }, [showLangPicker]);

  /** Switch language — stops playback, resets state, re-fetches lines */
  const switchLanguage = useCallback((langCode: string) => {
    stopAudio();
    if (wordTimerRef.current) { clearInterval(wordTimerRef.current); wordTimerRef.current = null; }
    if (autoAdvanceTimerRef.current) { clearTimeout(autoAdvanceTimerRef.current); autoAdvanceTimerRef.current = null; }
    setNarrating(false);
    setCurrentLineIdx(-1);
    setCurrentWordIdx(-1);
    setLineFinished(false);
    setActiveLang(langCode);
    setShowLangPicker(false);
    // Sync site-wide language with narration language
    changeSiteLanguage(langCode as LanguageCode);
  }, [stopAudio, changeSiteLanguage]);

  const clearWordTimer = useCallback(() => {
    if (wordTimerRef.current) {
      clearInterval(wordTimerRef.current);
      wordTimerRef.current = null;
    }
  }, []);

  const startWordTracking = useCallback((line: NarrationLine, durationMs: number) => {
    clearWordTimer();
    const words = line.display_text.split(/\s+/);
    if (!words.length) return;

    if (line.word_timestamps && line.word_timestamps.length > 0) {
      playbackStartRef.current = Date.now();
      wordTimerRef.current = setInterval(() => {
        const elapsed = Date.now() - playbackStartRef.current;
        const ts = line.word_timestamps!;
        let idx = ts.findIndex(w => elapsed >= w.start_ms && elapsed < w.end_ms);
        if (idx === -1 && elapsed >= ts[ts.length - 1]?.end_ms) idx = ts.length - 1;
        if (idx >= 0) setCurrentWordIdx(idx);
      }, 50);
    } else {
      const msPerWord = durationMs / words.length;
      playbackStartRef.current = Date.now();
      wordTimerRef.current = setInterval(() => {
        const elapsed = Date.now() - playbackStartRef.current;
        const idx = Math.min(Math.floor(elapsed / msPerWord), words.length - 1);
        setCurrentWordIdx(idx);
      }, 50);
    }
  }, [clearWordTimer]);

  /** Called when a line finishes playing — waits for Continue or auto-advances */
  const onLineComplete = useCallback((nextIdx: number) => {
    clearWordTimer();
    // Show all words as spoken
    setCurrentWordIdx(999);

    if (nextIdx >= lines.length) {
      // All lines done — stay on last line, show "finished" state
      setNarrating(false);
      setLineFinished(true);
      return;
    }

    if (autoAdvanceRef.current) {
      // Small pause then auto-advance
      autoAdvanceTimerRef.current = setTimeout(() => {
        setLineFinished(false);
        playLine(nextIdx);
      }, 800);
    } else {
      setLineFinished(true);
    }
  }, [clearWordTimer, lines.length]);

  const playLine = useCallback((idx: number) => {
    if (idx >= lines.length) {
      setNarrating(false);
      setLineFinished(true);
      return;
    }

    const line = lines[idx];
    setCurrentLineIdx(idx);
    setCurrentWordIdx(0);
    setLineFinished(false);
    setNarrating(true);

    if (!line.audio_url) {
      const textMs = Math.max(line.display_text.length * 40, 2000);
      startWordTracking(line, textMs);
      setTimeout(() => onLineComplete(idx + 1), textMs);
      return;
    }

    playAudioSrc(line.audio_url, () => {
      onLineComplete(idx + 1);
    }).then((durationSec) => {
      if (durationSec) {
        startWordTracking(line, durationSec * 1000);
      }
    });
  }, [lines, playAudioSrc, startWordTracking, onLineComplete]);

  const handlePlay = useCallback(() => {
    if (narrating) {
      // Pause: suspend audio context (preserves position) and pause word timer
      pauseAudio();
      clearWordTimer();
      setNarrating(false);
    } else if (isPaused) {
      // Resume from pause
      resumeAudio();
      setNarrating(true);
      // Restart word tracking from current position
      const line = currentLineIdx >= 0 ? lines[currentLineIdx] : null;
      if (line) {
        const durationMs = line.audio_duration_ms || Math.max(line.display_text.length * 40, 2000);
        // Approximate: restart tracking (won't be perfect but acceptable)
        startWordTracking(line, durationMs);
      }
    } else {
      // Fresh play
      if (!noOverlay) setOverlayOpen(true);
      setLineFinished(false);
      playLine(currentLineIdx >= 0 ? currentLineIdx : 0);
    }
  }, [narrating, isPaused, pauseAudio, resumeAudio, clearWordTimer, playLine, currentLineIdx, lines, startWordTracking, noOverlay]);

  const handleContinue = useCallback(() => {
    if (!overlayOpen && !noOverlay) return;
    const next = (currentLineIdx >= 0 ? currentLineIdx : -1) + 1;
    if (next < lines.length) {
      setLineFinished(false);
      playLine(next);
    }
  }, [overlayOpen, noOverlay, currentLineIdx, lines.length, playLine]);

  const handleClose = useCallback(() => {
    stopAudio();
    clearWordTimer();
    if (autoAdvanceTimerRef.current) { clearTimeout(autoAdvanceTimerRef.current); autoAdvanceTimerRef.current = null; }
    setNarrating(false);
    setOverlayOpen(false);
    setCurrentLineIdx(-1);
    setCurrentWordIdx(-1);
    setLineFinished(false);
    onClose?.();
  }, [stopAudio, clearWordTimer, onClose]);

  // ── autoStart ────────────────────────────────────────────────────────────
  // Callers that are themselves the play button (the HomeBeta "Listen to
  // Dignity" CTAs) mount this component only after the user has already
  // clicked. Without this, mounting rendered the inline "Dignity has something
  // to tell you" card — and because HomeBeta mounts us below the footer, that
  // card landed off-screen and waited for a second click the user never saw.
  // The CTA read as dead. Latched so it fires once per mount, and gated on
  // lines existing because playLine(0) against an empty list is a silent no-op
  // that would burn the latch before the fetch resolves.
  const autoStartedRef = useRef(false);
  useEffect(() => {
    if (!autoStart || autoStartedRef.current) return;
    if (loading || lines.length === 0) return;
    autoStartedRef.current = true;
    handlePlay();
  }, [autoStart, loading, lines.length, handlePlay]);

  // Cleanup on unmount — stop audio and timers when component is removed from DOM
  useEffect(() => {
    return () => {
      stopAudio();
      clearWordTimer();
      if (autoAdvanceTimerRef.current) { clearTimeout(autoAdvanceTimerRef.current); autoAdvanceTimerRef.current = null; }
    };
  }, [stopAudio, clearWordTimer]);

  const handleSkipForward = useCallback(() => {
    stopAudio();
    clearWordTimer();
    const next = currentLineIdx + 1;
    if (next < lines.length) {
      setLineFinished(false);
      playLine(next);
    } else {
      setNarrating(false);
      setLineFinished(true);
    }
  }, [stopAudio, clearWordTimer, currentLineIdx, lines.length, playLine]);

  const handleSkipBack = useCallback(() => {
    stopAudio();
    clearWordTimer();
    const prev = Math.max(0, currentLineIdx - 1);
    setLineFinished(false);
    playLine(prev);
  }, [stopAudio, clearWordTimer, currentLineIdx, playLine]);

  const jumpToLine = useCallback((idx: number) => {
    stopAudio();
    clearWordTimer();
    setLineFinished(false);
    playLine(idx);
  }, [stopAudio, clearWordTimer, playLine]);

  if (loading || lines.length === 0) return null;

  const currentLine = currentLineIdx >= 0 ? lines[currentLineIdx] : null;
  const words = currentLine ? currentLine.display_text.split(/\s+/) : [];
  const isLastLine = currentLineIdx >= lines.length - 1;
  const allDone = !narrating && lineFinished && isLastLine;

  // ── Inline trigger — a compact, self-contained card (shown in the Dignity
  //    section). Deliberately understated: one row, no auto-pulse, and it no
  //    longer previews the first narration line (which read as a self-promo).
  const inlineTrigger = (
    <div style={{ fontFamily: sans, display: "flex", justifyContent: "center", padding: "4px 0" }}>
      <button
        onClick={handlePlay}
        aria-label="Play Dignity's guide to this page"
        style={{
          display: "flex", alignItems: "center", gap: 13,
          padding: "10px 18px 10px 11px", borderRadius: 999,
          background: isDark ? "rgba(0,16,52,0.45)" : "rgba(0,40,104,0.035)",
          border: `1px solid ${T.goldDim}`,
          cursor: "pointer", maxWidth: 420, width: "100%",
          transition: "border-color 0.2s ease, background 0.2s ease, transform 0.2s ease",
        }}
        onMouseEnter={e => { e.currentTarget.style.borderColor = T.gold; e.currentTarget.style.transform = "translateY(-1px)"; }}
        onMouseLeave={e => { e.currentTarget.style.borderColor = T.goldDim; e.currentTarget.style.transform = "translateY(0)"; }}
      >
        <span style={{
          flexShrink: 0, width: 38, height: 38, borderRadius: "50%",
          background: `linear-gradient(135deg, ${T.blue} 0%, #003A9E 100%)`,
          border: `1.5px solid ${T.gold}`, color: T.gold,
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <Play size={16} style={{ marginLeft: 2 }} />
        </span>
        <span style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 1, minWidth: 0 }}>
          <span style={{ fontFamily: serif, fontSize: 15, color: T.gold, fontStyle: "italic", lineHeight: 1.2 }}>
            Dignity has something to tell you
          </span>
          <span style={{ fontSize: 10.5, fontFamily: sans, color: T.textMuted, letterSpacing: "0.4px" }}>
            {hasAudio ? "Listen to a" : "Read a"} quick guide to this page &middot; {lines.length} lines
            {availableLangs.length > 1 && ` \u00B7 ${availableLangs.length} languages`}
          </span>
        </span>
      </button>
    </div>
  );

  // \u2500\u2500 Compact trigger \u2014 a slim horizontal button for the two-guide hub \u2500\u2500
  // The hub surface is ALWAYS a dark navy gradient (both site themes), so this
  // trigger uses fixed light-on-dark colors rather than the theme-following T.*
  // \u2014 otherwise light mode renders dark text on the dark hub (unreadable).
  const compactTrigger = (
    <button
      onClick={handlePlay}
      className="group/dignity"
      style={{
        display: "flex", alignItems: "center", gap: 10, width: "100%", textAlign: "left",
        padding: "10px 12px", borderRadius: 10, cursor: "pointer",
        background: "rgba(0,16,52,0.55)",
        border: "1px solid rgba(197,165,90,0.28)",
        fontFamily: sans, transition: "border-color 0.2s, background 0.2s",
      }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = "#C5A55A"; e.currentTarget.style.background = "rgba(197,165,90,0.12)"; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(197,165,90,0.28)"; e.currentTarget.style.background = "rgba(0,16,52,0.55)"; }}
    >
      <span style={{
        flexShrink: 0, width: 32, height: 32, borderRadius: "50%",
        background: "linear-gradient(135deg, #002868 0%, #003A9E 100%)",
        border: "1.5px solid #C5A55A", color: "#C5A55A",
        display: "flex", alignItems: "center", justifyContent: "center",
        transition: "transform 0.2s ease",
      }} className="group-hover/dignity:scale-110">
        <Play size={14} style={{ marginLeft: 1 }} />
      </span>
      <span style={{ minWidth: 0, flex: 1 }}>
        <span style={{ display: "block", fontFamily: serif, fontSize: 14, fontWeight: 600, color: "#f0ebe3", lineHeight: 1.25 }}>
          {label || "Listen"}
        </span>
        <span style={{ display: "block", fontSize: 10, letterSpacing: "0.5px", textTransform: "uppercase", fontWeight: 700, color: "#C5A55A", marginTop: 2 }}>
          {sublabel || `${lines.length} lines \u00B7 ${hasAudio ? "Listen" : "Read"}`}
        </span>
      </span>
      <ChevronRight size={16} style={{ flexShrink: 0, color: "rgba(240,235,227,0.6)" }} className="transition-transform group-hover/dignity:translate-x-0.5" />
    </button>
  );

  // ── Fixed bottom overlay (the visual novel dialogue box) ──
  const overlay = overlayOpen ? createPortal(
    <div
      style={{
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 10001, // Above footer (z-10000)
        fontFamily: sans,
        // Offset above the app footer bar
        paddingBottom: `calc(${FOOTER_MOBILE}px + ${FOOTER_PAD}px + env(safe-area-inset-bottom, 0px))`,
        animation: "dignitySlideUp 0.35s ease-out",
      }}
      className="dignity-overlay-root"
    >
      <style>{`
        @keyframes dignitySlideUp { from{transform:translateY(100%);opacity:0} to{transform:translateY(0);opacity:1} }
        @keyframes dignityWordGlow { 0%{text-shadow:0 0 6px rgba(197,165,90,0.5)} 100%{text-shadow:0 0 2px rgba(197,165,90,0.2)} }
        @keyframes dignityFadeIn { from{opacity:0;transform:translateY(4px)} to{opacity:1;transform:translateY(0)} }
        @keyframes dignityBlink { 0%,100%{opacity:1} 50%{opacity:0.3} }
        .dignity-active-word { animation: dignityWordGlow 0.8s ease-in-out alternate infinite; }
        .dignity-line-fade { animation: dignityFadeIn 0.3s ease-out; }
        .dignity-continue-blink { animation: dignityBlink 1.2s ease-in-out infinite; }
        @media (min-width: 1024px) {
          .dignity-overlay-root { padding-bottom: calc(${FOOTER_DESKTOP}px + ${FOOTER_PAD}px + env(safe-area-inset-bottom, 0px)) !important; }
        }
      `}</style>

      {/* Backdrop fade */}
      <div style={{
        position: "absolute", bottom: 0, left: 0, right: 0, height: 300,
        background: isDark
          ? "linear-gradient(to top, rgba(0,8,24,0.85) 0%, transparent 100%)"
          : "linear-gradient(to top, rgba(0,20,60,0.15) 0%, transparent 100%)",
        pointerEvents: "none",
      }} />

      {/* ── Dialogue Panel — spread wide like a classic VN dialogue box ── */}
      <div style={{
        position: "relative",
        maxWidth: 1160,
        margin: "0 auto",
        padding: "0 12px",
      }}>
        <div style={{
          background: T.dialogBg,
          border: `2px solid ${T.dialogBorder}`,
          borderRadius: 16,
          boxShadow: T.dialogShadow,
        }}>
          {/* ── Header bar ── */}
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            padding: "8px 16px",
            background: `linear-gradient(90deg, ${T.blue} 0%, #003A9E 100%)`,
            borderBottom: `2px solid ${T.gold}`,
            borderRadius: "14px 14px 0 0",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{
                width: 8, height: 8, borderRadius: "50%",
                background: isPlaying ? "#4ade80" : isPaused ? "#facc15" : T.gold,
                boxShadow: isPlaying ? "0 0 10px rgba(74,222,128,0.6)" : isPaused ? "0 0 8px rgba(250,204,21,0.4)" : "none",
                transition: "all 0.3s",
              }} />
              <span style={{ fontFamily: sans, fontSize: 11, fontWeight: 800, color: T.gold, letterSpacing: "2.5px", textTransform: "uppercase" }}>
                Dignity
              </span>
              <span style={{ fontSize: 10, color: "rgba(240,235,227,0.85)", fontFamily: sans, letterSpacing: "1px" }}>
                Financial Healthcare Advisor
              </span>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              {/* Subtitle / no-audio indicator */}
              {!hasAudio && lines.length > 0 && (
                <span style={{
                  fontSize: 9, fontWeight: 700, letterSpacing: "1px", textTransform: "uppercase",
                  padding: "2px 6px", borderRadius: 4,
                  background: "rgba(250,204,21,0.15)", color: "#facc15",
                  fontFamily: sans,
                }}>
                  SUB
                </span>
              )}

              <span style={{ fontSize: 11, fontFamily: sans, fontWeight: 600, color: "rgba(197,165,90,0.6)" }}>
                {currentLineIdx >= 0 ? `${currentLineIdx + 1}/${lines.length}` : `${lines.length} lines`}
              </span>

              {/* Language picker */}
              {availableLangs.length > 1 && (
                <div style={{ position: "relative" }}>
                  <button
                    onClick={() => setShowLangPicker(v => !v)}
                    title="Change language"
                    style={{
                      background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.15)",
                      borderRadius: 6, padding: "3px 8px", cursor: "pointer",
                      display: "flex", alignItems: "center", gap: 4,
                      transition: "background 0.15s", fontSize: 11,
                      color: "rgba(255,255,255,0.8)", fontFamily: sans,
                    }}
                    onMouseEnter={e => (e.currentTarget.style.background = "rgba(255,255,255,0.2)")}
                    onMouseLeave={e => (e.currentTarget.style.background = "rgba(255,255,255,0.1)")}
                  >
                    {langFlagImg(activeLang, 16)}
                    <span style={{ fontWeight: 600, letterSpacing: "0.5px" }}>{activeLang.toUpperCase()}</span>
                  </button>

                  {showLangPicker && (() => {
                    const q = langQuery.trim().toLowerCase();
                    const filtered = q
                      ? availableLangs.filter(l =>
                          l.name.toLowerCase().includes(q) || l.code.toLowerCase().includes(q))
                      : availableLangs;
                    return (
                    <div
                      onClick={e => e.stopPropagation()}
                      style={{
                        position: "absolute", bottom: "calc(100% + 6px)", right: 0,
                        background: isDark ? "rgba(0,16,52,0.98)" : "rgba(255,255,255,0.99)",
                        border: `1px solid ${T.dialogBorder}`, borderRadius: 10,
                        boxShadow: "0 8px 32px rgba(0,0,0,0.4)", zIndex: 20,
                        padding: 4, minWidth: 220, maxWidth: 260,
                        display: "flex", flexDirection: "column",
                      }}
                    >
                      <input
                        ref={langSearchRef}
                        type="text"
                        value={langQuery}
                        onChange={e => setLangQuery(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === "Escape") { setShowLangPicker(false); }
                          else if (e.key === "Enter" && filtered.length > 0) { switchLanguage(filtered[0].code); }
                        }}
                        placeholder="Search language\u2026"
                        style={{
                          margin: 4, padding: "6px 8px",
                          border: `1px solid ${T.dialogBorder}`, borderRadius: 6,
                          background: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)",
                          color: T.text, fontFamily: sans, fontSize: 12, outline: "none",
                        }}
                      />
                      <div style={{ maxHeight: 280, overflowY: "auto", paddingRight: 2 }}>
                        {filtered.length === 0 ? (
                          <div style={{
                            padding: "12px", fontSize: 11, color: T.textMuted,
                            fontFamily: sans, textAlign: "center",
                          }}>
                            No matches
                          </div>
                        ) : filtered.map(lang => (
                        <button
                          key={lang.code}
                          onClick={() => switchLanguage(lang.code)}
                          style={{
                            display: "flex", alignItems: "center", gap: 8, width: "100%",
                            padding: "8px 12px", border: "none", cursor: "pointer",
                            background: lang.code === activeLang
                              ? (isDark ? "rgba(197,165,90,0.15)" : "rgba(0,40,104,0.08)")
                              : "transparent",
                            borderRadius: 6, transition: "background 0.15s",
                            fontFamily: sans, textAlign: "left",
                          }}
                          onMouseEnter={e => {
                            if (lang.code !== activeLang) e.currentTarget.style.background = isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)";
                          }}
                          onMouseLeave={e => {
                            if (lang.code !== activeLang) e.currentTarget.style.background = "transparent";
                          }}
                        >
                          {langFlagImg(lang.code, 20) || <Globe size={16} />}
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 12, fontWeight: 600, color: T.text }}>{lang.name}</div>
                            <div style={{ fontSize: 10, color: T.textMuted }}>
                              {lang.lineCount} lines
                            </div>
                          </div>
                          {lang.code === activeLang && (
                            <div style={{ width: 6, height: 6, borderRadius: "50%", background: T.gold, flexShrink: 0 }} />
                          )}
                        </button>
                        ))}
                      </div>
                    </div>
                    );
                  })()}
                </div>
              )}

              {/* Single language flag (when only 1 language) */}
              {availableLangs.length === 1 && (
                <span style={{ lineHeight: 1 }} title={availableLangs[0].name}>
                  {langFlagImg(activeLang, 16)}
                </span>
              )}

              {/* Close button */}
              <button
                onClick={handleClose}
                title="Close"
                style={{
                  background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.15)",
                  borderRadius: 6, padding: "4px 8px", cursor: "pointer",
                  display: "flex", alignItems: "center", gap: 4,
                  transition: "background 0.15s", fontSize: 10, fontWeight: 600,
                  color: "rgba(255,255,255,0.7)", fontFamily: sans,
                }}
                onMouseEnter={e => (e.currentTarget.style.background = "rgba(255,255,255,0.2)")}
                onMouseLeave={e => (e.currentTarget.style.background = "rgba(255,255,255,0.1)")}
              >
                Close <X size={12} />
              </button>
            </div>
          </div>

          {/* ── Content: 3D Portrait + Dialogue ── */}
          <div style={{
            display: "flex", gap: 16,
            padding: "14px 20px 12px",
            alignItems: "flex-start",
          }}>
            {/* Portrait — live GLB preview (model-viewer is defined globally
                by /model-viewer.min.js from index.html, so this renders with
                no extra import; same local asset as the boot splash). */}
            <div style={{
              flexShrink: 0, width: 84, height: 108,
              borderRadius: 12, overflow: "hidden",
              border: `2px solid ${T.gold}`,
              boxShadow: `0 4px 20px rgba(197,165,90,0.25)`,
              background: isDark
                ? "radial-gradient(120% 90% at 50% 35%, rgba(197,165,90,0.14) 0%, rgba(197,165,90,0.04) 100%)"
                : "#FFF8E7",
            }}>
              {/* @ts-expect-error — model-viewer web component */}
              <model-viewer
                src="/consumer/dignity_bill_lite.glb"
                alt="Dignity"
                loading="eager"
                interaction-prompt="none"
                disable-zoom
                camera-orbit="0deg 80deg 95%"
                exposure="1.1"
                shadow-intensity="0"
                style={{ width: "100%", height: "100%", background: "transparent", "--poster-color": "transparent" } as React.CSSProperties}
              />
              {/* No auto-rotate: Dignity faces the reader like a normal VN
                  portrait (the 360° turntable read as him wandering off). */}
            </div>

            {/* Dialogue text area — VN-style window: exactly three visual
                lines of the current paragraph are visible; the box auto-
                scrolls to follow the highlighted word (see dialogueBoxRef
                effect). No wall of text, no previous-line log. */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontFamily: serif, fontSize: 16, fontWeight: 400, color: T.gold, marginBottom: 6, letterSpacing: "0.3px" }}>
                Dignity
              </div>

              {currentLine ? (
                <div key={currentLineIdx}>
                  <div
                    ref={dialogueBoxRef}
                    className="dignity-line-fade"
                    style={{
                      fontSize: "clamp(15px, 2vw, 18px)", lineHeight: 1.6, fontFamily: serif,
                      minHeight: 48,
                      maxHeight: "4.8em",           // 3 visual lines × 1.6 line-height
                      overflowY: "hidden",
                      position: "relative",          // offsetTop of word spans → relative to this box
                    }}
                  >
                    {words.map((word, i) => (
                      <span
                        key={i}
                        className={i === currentWordIdx ? "dignity-active-word" : ""}
                        style={{
                          color: i === currentWordIdx ? T.activeWord
                            : i < currentWordIdx ? T.spokenWord
                            : T.unspokenWord,
                          fontWeight: i === currentWordIdx ? 700 : 400,
                          transition: "color 0.15s ease, font-weight 0.15s ease",
                        }}
                      >
                        {word}{i < words.length - 1 ? " " : ""}
                      </span>
                    ))}
                  </div>
                </div>
              ) : (
                <p style={{ fontSize: 15, lineHeight: 1.7, fontFamily: serif, color: T.textMuted, fontStyle: "italic", margin: 0 }}>
                  Press play to start...
                </p>
              )}
            </div>
          </div>

          {/* ── Line progress bar ── */}
          <div style={{ display: "flex", gap: 3, padding: "0 20px", marginBottom: 10 }}>
            {lines.map((_, i) => (
              <button
                key={i}
                onClick={() => jumpToLine(i)}
                onMouseEnter={() => setHoveredLine(i)}
                onMouseLeave={() => setHoveredLine(null)}
                title={`Line ${i + 1}`}
                style={{
                  flex: 1, height: i === currentLineIdx ? 4 : 3, borderRadius: 2,
                  background: i === currentLineIdx ? T.gold
                    : i < currentLineIdx ? (isDark ? "rgba(197,165,90,0.4)" : "rgba(0,40,104,0.3)")
                    : (isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.08)"),
                  border: "none", cursor: "pointer", padding: 0,
                  transition: "all 0.2s ease",
                  transform: hoveredLine === i ? "scaleY(2)" : "scaleY(1)",
                }}
              />
            ))}
          </div>

          {/* ── Controls bar ── */}
          <div style={{
            display: "flex", alignItems: "center",
            padding: "0 20px 14px", gap: 6,
          }}>
            {/* Skip back */}
            <button
              onClick={handleSkipBack}
              disabled={currentLineIdx <= 0}
              title="Previous line"
              style={{
                background: "none", border: "none",
                cursor: currentLineIdx > 0 ? "pointer" : "default",
                padding: 6, display: "flex", alignItems: "center",
                opacity: currentLineIdx > 0 ? 0.7 : 0.2, transition: "opacity 0.2s",
              }}
              onMouseEnter={e => { if (currentLineIdx > 0) e.currentTarget.style.opacity = "1"; }}
              onMouseLeave={e => { if (currentLineIdx > 0) e.currentTarget.style.opacity = "0.7"; }}
            >
              <SkipBack size={16} color={T.gold} />
            </button>

            {/* Play/Pause */}
            <button
              onClick={handlePlay}
              title={narrating ? "Pause" : (isPaused ? "Resume" : "Play")}
              style={{
                width: 36, height: 36, borderRadius: "50%",
                background: narrating
                  ? `linear-gradient(135deg, ${T.gold} 0%, #D4B86A 100%)`
                  : `linear-gradient(135deg, ${T.blue} 0%, #003A9E 100%)`,
                border: `2px solid ${narrating ? (isDark ? "rgba(255,255,255,0.15)" : "rgba(0,40,104,0.2)") : T.gold}`,
                color: narrating ? T.blue : T.gold,
                display: "flex", alignItems: "center", justifyContent: "center",
                cursor: "pointer", padding: 0,
                boxShadow: "0 2px 12px rgba(197,165,90,0.3)", transition: "transform 0.15s ease",
              }}
              onMouseEnter={e => (e.currentTarget.style.transform = "scale(1.08)")}
              onMouseLeave={e => (e.currentTarget.style.transform = "scale(1)")}
            >
              {narrating ? <Pause size={14} /> : <Play size={14} style={{ marginLeft: 2 }} />}
            </button>

            {/* Skip forward */}
            <button
              onClick={handleSkipForward}
              disabled={isLastLine}
              title="Next line"
              style={{
                background: "none", border: "none",
                cursor: !isLastLine ? "pointer" : "default",
                padding: 6, display: "flex", alignItems: "center",
                opacity: !isLastLine ? 0.7 : 0.2, transition: "opacity 0.2s",
              }}
              onMouseEnter={e => { if (!isLastLine) e.currentTarget.style.opacity = "1"; }}
              onMouseLeave={e => { if (!isLastLine) e.currentTarget.style.opacity = "0.7"; }}
            >
              <SkipForward size={16} color={T.gold} />
            </button>

            {/* Spacer */}
            <div style={{ flex: 1 }} />

            {/* Continue / Finish button */}
            {lineFinished && (
              allDone ? (
                <button
                  onClick={handleClose}
                  style={{
                    fontFamily: sans, fontSize: 12, fontWeight: 700,
                    padding: "6px 16px", borderRadius: 8,
                    background: `linear-gradient(135deg, ${T.gold} 0%, #D4B86A 100%)`,
                    color: T.blue, border: "none", cursor: "pointer",
                    display: "flex", alignItems: "center", gap: 6,
                    boxShadow: "0 2px 12px rgba(197,165,90,0.3)",
                    transition: "transform 0.15s",
                  }}
                  onMouseEnter={e => (e.currentTarget.style.transform = "scale(1.04)")}
                  onMouseLeave={e => (e.currentTarget.style.transform = "scale(1)")}
                >
                  Finish <X size={13} />
                </button>
              ) : (
                <button
                  onClick={handleContinue}
                  className="dignity-continue-blink"
                  style={{
                    fontFamily: sans, fontSize: 12, fontWeight: 700,
                    padding: "6px 16px", borderRadius: 8,
                    background: `linear-gradient(135deg, ${T.gold} 0%, #D4B86A 100%)`,
                    color: T.blue, border: "none", cursor: "pointer",
                    display: "flex", alignItems: "center", gap: 4,
                    boxShadow: "0 2px 12px rgba(197,165,90,0.3)",
                    transition: "transform 0.15s",
                  }}
                  onMouseEnter={e => (e.currentTarget.style.transform = "scale(1.04)")}
                  onMouseLeave={e => (e.currentTarget.style.transform = "scale(1)")}
                >
                  Continue <ChevronRight size={14} />
                </button>
              )
            )}

            {/* Auto-advance toggle */}
            <button
              onClick={() => setAutoAdvance(a => !a)}
              title={autoAdvance ? "Auto-advance ON (click to stop after each line)" : "Auto-advance OFF (click to auto-play)"}
              style={{
                fontFamily: sans, fontSize: 10, fontWeight: 600,
                padding: "4px 8px", borderRadius: 6,
                background: autoAdvance ? "rgba(197,165,90,0.15)" : "transparent",
                color: autoAdvance ? T.gold : T.textMuted,
                border: `1px solid ${autoAdvance ? "rgba(197,165,90,0.3)" : "rgba(255,255,255,0.1)"}`,
                cursor: "pointer", letterSpacing: "0.5px",
                transition: "all 0.2s",
              }}
            >
              AUTO
            </button>

            {/* Volume */}
            <div style={{ position: "relative" }}>
              <button
                onClick={() => setShowVolume(v => !v)}
                title="Volume"
                style={{
                  background: "none", border: "none", cursor: "pointer",
                  padding: 6, display: "flex", alignItems: "center", opacity: 0.6,
                }}
                onMouseEnter={e => (e.currentTarget.style.opacity = "1")}
                onMouseLeave={e => (e.currentTarget.style.opacity = "0.6")}
              >
                {volume === 0 ? <VolumeX size={15} color={T.textMuted} /> : <Volume2 size={15} color={T.gold} />}
              </button>

              {showVolume && (
                <div style={{
                  position: "absolute", bottom: "100%", right: -10, marginBottom: 8,
                  background: isDark ? "rgba(0,16,52,0.97)" : "rgba(255,255,255,0.98)",
                  border: `1px solid ${T.dialogBorder}`,
                  borderRadius: 8, padding: "8px 14px",
                  boxShadow: "0 4px 20px rgba(0,0,0,0.2)", zIndex: 10,
                }}>
                  <input
                    type="range" min={0} max={1} step={0.01} value={volume}
                    onChange={e => setVolume(parseFloat(e.target.value))}
                    style={{ width: 80, accentColor: T.gold }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  ) : null;

  return (
    <>
      {(!overlayOpen || noOverlay) && (compact ? compactTrigger : inlineTrigger)}
      {/* In noOverlay mode, show current line text + word highlighting inline
          below the trigger — same 3-visual-line VN window as the overlay
          (dialogueBoxRef auto-follows the active word; only one of the two
          paths mounts at a time, so the ref is shared safely). */}
      {noOverlay && narrating && currentLineIdx >= 0 && lines[currentLineIdx] && (
        <div style={{
          margin: "16px 0",
          padding: "16px 20px",
          background: isDark ? "rgba(0,20,60,0.9)" : "rgba(0,20,60,0.95)",
          borderRadius: 10,
          border: "1.5px solid rgba(197,165,90,0.3)",
        }}>
          <div
            key={currentLineIdx}
            ref={dialogueBoxRef}
            style={{
              fontFamily: serif,
              fontSize: "clamp(15px, 1.8vw, 18px)",
              lineHeight: 1.7,
              color: "#f0ebe3",
              maxHeight: "5.1em",           // 3 visual lines × 1.7 line-height
              overflowY: "hidden",
              position: "relative",
            }}
          >
            {lines[currentLineIdx].display_text.split(' ').map((word, wi) => (
              <span key={wi} className={wi === currentWordIdx ? "dignity-active-word" : ""} style={{
                color: wi <= currentWordIdx ? '#C5A55A' : '#f0ebe3',
                transition: 'color 0.15s',
              }}>
                {word}{' '}
              </span>
            ))}
          </div>
        </div>
      )}
      {!noOverlay && overlay}
    </>
  );
}
